"use client";

import { usePathname } from "next/navigation";
import type { ProductDetail } from "./types";
import { LockedListingActionCard } from "./listing-action-locked-card";
import { RecycleOfferForm } from "./recycle-offer-form";
import { getEnabledTrucksFromCapacities } from "./recycle-truck-capacities";
import { Card, CardContent } from "@/components/ui/card";
import { TRIAL_DAYS } from "@shared/subscriptions/plan-content";
import { useClaimGateShown } from "./use-claim-gate-shown";
import { useListingQuery } from "./listing-query";
import { settingsHref } from "@shared/routing/settings-path";

export type RecycleActionListing = Pick<
  ProductDetail,
  | "_id"
  | "intent"
  | "isFreePickup"
  | "minOrderQuantity"
  | "availableToSell"
  | "vendorHasPickupAddress"
  | "truckCapacities"
  | "targetRebateCents"
>;

export function ListingRecycleAction({
  data,
  hasCompany,
  canRespond,
}: {
  data: RecycleActionListing;
  hasCompany: boolean;
  canRespond: boolean;
}) {
  const minOrderQuantity = data.minOrderQuantity || 1;
  const pickupOnly = data.isFreePickup;
  const unavailable = recycleAvailabilityNotice(data);
  useClaimGateShown(
    data._id,
    hasCompany && !canRespond && !unavailable ? "upgrade" : null,
  );
  if (unavailable) return unavailable;
  if (!hasCompany) {
    return <RecycleSetupGate productId={data._id} />;
  }
  if (!canRespond) {
    return <RecycleUpgradeGate isFreePickup={data.isFreePickup} />;
  }

  return (
    <RecycleOfferForm
      productId={data._id}
      minOrderQuantity={minOrderQuantity}
      maxQuantity={data.availableToSell ?? 0}
      pickupAvailable={data.vendorHasPickupAddress}
      enabledTrucks={getEnabledTrucksFromCapacities(data.truckCapacities)}
      targetRebateCents={data.targetRebateCents ?? undefined}
      pickupOnly={pickupOnly}
    />
  );
}

function isRecycleSoldOut(
  data: Pick<ProductDetail, "availableToSell" | "minOrderQuantity">,
): boolean {
  return (data.availableToSell ?? 0) < (data.minOrderQuantity || 1);
}

export function recycleAvailabilityNotice(
  data: Pick<
    ProductDetail,
    "minOrderQuantity" | "availableToSell" | "isFreePickup" | "vendorHasPickupAddress"
  >,
) {
  if (isRecycleSoldOut(data)) {
    return (
      <RecycleSoldOutNotice
        availableToSell={data.availableToSell ?? 0}
        minOrderQuantity={data.minOrderQuantity || 1}
      />
    );
  }
  if (data.isFreePickup && !data.vendorHasPickupAddress) {
    return (
      <RecycleNotice
        title="Pickup not available yet"
        body="This listing is collected by the buyer, but the seller hasn't added a pickup address. Check back later — claims open once a pickup location is set."
      />
    );
  }
  return null;
}

function RecycleSoldOutNotice({
  availableToSell,
  minOrderQuantity,
}: {
  availableToSell: number;
  minOrderQuantity: number;
}) {
  const isFullyClaimed = availableToSell <= 0;
  return (
    <RecycleNotice
      title={isFullyClaimed ? "Fully claimed" : "Below the minimum claim"}
      body={
        isFullyClaimed
          ? "All units on this listing have been claimed. Check back later — the seller may add more."
          : `Only ${availableToSell} left, below this listing's minimum of ${minOrderQuantity}. Check back later — the seller may add more.`
      }
    />
  );
}

function RecycleNotice({ title, body }: { title: string; body: string }) {
  return (
    <Card className="bg-muted/30">
      <CardContent className="space-y-1 p-4">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

function RecycleSetupGate({
  productId,
}: {
  productId: ProductDetail["_id"];
}) {
  const pathname = usePathname();
  const searchParams = useListingQuery();
  useClaimGateShown(productId, "setup");

  return (
    <LockedListingActionCard
      pathname={pathname}
      searchParams={searchParams}
      description="Finish setting up your company profile to respond."
      buildPrimaryHref={(returnTo) =>
        `/setup?returnTo=${encodeURIComponent(returnTo)}&intent=buy`
      }
      primaryLabel="Complete Setup"
      secondaryHref={undefined}
      secondaryLabel={undefined}
    />
  );
}

function RecycleUpgradeGate({ isFreePickup }: { isFreePickup: boolean }) {
  const pathname = usePathname();
  const searchParams = useListingQuery();

  return (
    <LockedListingActionCard
      pathname={pathname}
      searchParams={searchParams}
      description={
        isFreePickup
          ? `Claiming free pickups is part of Pro, which starts with a ${TRIAL_DAYS}-day free trial.`
          : `Making an offer on recycle listings is part of Pro, which starts with a ${TRIAL_DAYS}-day free trial.`
      }
      buildPrimaryHref={(returnTo) =>
        settingsHref("plans", "/dashboard", { returnTo })
      }
      primaryLabel="See Pro"
      secondaryHref={undefined}
      secondaryLabel={undefined}
    />
  );
}
